import { useOutletContext } from "react-router-dom";
import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { API_URL } from "../../config/api";
import DatePicker from "react-datepicker";
import ToastMessage from "../../config/ToastMessage";
import "react-datepicker/dist/react-datepicker.css";

const ADMIN_ROLES = ["super_admin", "org_admin", "training_partner_admin"];

const CreateScheduleForm = () => {
  const navigate = useNavigate();
  const dropdownRef = useRef(null);
  const { refetchUserData } = useOutletContext() || {};

  const [currentUserRole, setCurrentUserRole] = useState(null); // ✅ track role
  const [instructors, setInstructors] = useState([]); // ✅ instructor list

  const [formData, setFormData] = useState({
    course: "",
    courseDuration: null,
    mode: "In Person / Regular",
    location: "",
    startDate: null,
    endDate: null,
    startTime: "",
    endTime: "",
    enableReminders: true,
    reminderTime: "",
    discountApplicable: false,
    discountType: "",
    discountValue: "",
    discountValidFrom: null,
    discountValidTo: null,
    instructor_id: "", // ✅ added
  });

  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState({ show: false, type: "", message: "" });
  const [errors, setErrors] = useState({});
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const isAdminRole = ADMIN_ROLES.includes(currentUserRole);

  const toYMD = (date) => {
    if (!date) return null;
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const formatTime = (t) => (t.length === 5 ? `${t}:00` : t);

  const showToast = (type, message) => setToast({ show: true, type, message });
  const closeToast = () => setToast({ show: false, type: "", message: "" });

  // ✅ Fetch current user role
  useEffect(() => {
    const fetchCurrentUser = async () => {
      try {
        const token = localStorage.getItem("access_token");
        if (!token) return;

        const res = await fetch(`${API_URL}/current_user`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json();
        const role = data?.roles?.[0]?.name || null;
        setCurrentUserRole(role);
      } catch (err) {
        console.error("Failed to fetch current user:", err);
      }
    };

    fetchCurrentUser();
  }, []);

  // ✅ Fetch instructors only if admin role
  useEffect(() => {
    if (!isAdminRole) return;

    const fetchInstructors = async () => {
      try {
        const token = localStorage.getItem("access_token");
        if (!token) return;

        const res = await fetch(
          `${API_URL}/user/get_all_users?role=instructor`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        const data = await res.json();
        setInstructors(data?.data || []);
      } catch (err) {
        console.error("Failed to fetch instructors:", err);
      }
    };

    fetchInstructors();
  }, [isAdminRole]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Fetch courses
  useEffect(() => {
    const fetchCourses = async () => {
      try {
        const token = localStorage.getItem("access_token");
        if (!token) {
          showToast("error", "You are not logged in. Please login first.");
          return;
        }
        const res = await fetch(`${API_URL}/courses/get_user_courses`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json();
        if (res.ok && data.success) {
          setCourses(data.data);
        } else {
          showToast("error", data.message || "Failed to fetch courses");
        }
      } catch (err) {
        console.error("Error fetching courses:", err);
        showToast("error", "Error fetching courses. Please try again later.");
      }
    };
    fetchCourses();
  }, []);

  const validateForm = () => {
    const newErrors = {};

    const requiredFields = [
      { field: "course", message: "This field is required" },
      { field: "location", message: "This field is required" },
      { field: "startDate", message: "This field is required" },
      { field: "endDate", message: "This field is required" },
      { field: "startTime", message: "This field is required" },
      { field: "endTime", message: "This field is required" },
    ];

    // ✅ Instructor required only for admin roles
    if (isAdminRole) {
      requiredFields.push({
        field: "instructor_id",
        message: "Please select an instructor",
      });
    }

    requiredFields.forEach(({ field, message }) => {
      if (!formData[field]) newErrors[field] = message;
    });

    if (formData.discountApplicable) {
      if (!formData.discountType)
        newErrors.discountType = "Please select a discount type";
      if (!formData.discountValue) {
        newErrors.discountValue = "Discount value is required";
      } else {
        const discountVal = parseFloat(formData.discountValue);
        if (discountVal < 1 || discountVal > 100)
          newErrors.discountValue = "Discount value must be between 1 and 100";
      }
      if (!formData.discountValidFrom)
        newErrors.discountValidFrom = "Discount valid from date is required";
      if (!formData.discountValidTo)
        newErrors.discountValidTo = "Discount valid to date is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      showToast("error", "Please fix the errors below before submitting");
      return;
    }

    setLoading(true);

    try {
      const token = localStorage.getItem("access_token");
      if (!token) {
        showToast("error", "No access token found. Please login again.");
        setLoading(false);
        return;
      }

      const reminderMap = { "30min": 30, "1hr": 60, "2hr": 120, "1day": 1440 };

      const payload = {
        course_id: Number(formData.course),
        mode: formData.mode,
        location: formData.location,
        start_date: toYMD(formData.startDate),
        end_date: toYMD(formData.endDate),
        start_time: formatTime(formData.startTime),
        end_time: formatTime(formData.endTime),
        enable_reminders: formData.enableReminders,
        reminder_time: formData.reminderTime
          ? reminderMap[formData.reminderTime]
          : 30,
        discount_applicable: formData.discountApplicable,
        discount_type: formData.discountApplicable
          ? formData.discountType
          : null,
        discount_value: formData.discountApplicable
          ? parseFloat(formData.discountValue)
          : null,
        discount_valid_from: formData.discountApplicable
          ? `${toYMD(formData.discountValidFrom)}T00:00:00`
          : null,
        discount_valid_to: formData.discountApplicable
          ? `${toYMD(formData.discountValidTo)}T23:59:59`
          : null,
        // ✅ Include instructor_id only for admin roles
        ...(isAdminRole && formData.instructor_id
          ? { instructor_id: Number(formData.instructor_id) }
          : {}),
      };

      console.log("course schedule Payload:", payload);

      const response = await fetch(`${API_URL}/course-schedules/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (response.ok) {
        showToast("success", data.message || "Schedule created successfully!");
        if (refetchUserData) await refetchUserData();
        setTimeout(() => navigate("/program_schdule"), 1500);
      } else {
        let errorMsg = "";
        if (data.detail) {
          errorMsg = Array.isArray(data.detail)
            ? data.detail.map((e) => e.msg).join(", ")
            : data.detail;
        } else {
          errorMsg = data.message || "Failed to create schedule";
        }
        showToast("error", errorMsg);
      }
    } catch (error) {
      console.error(error);
      showToast("error", "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => navigate("/program_schdule");

  const clearFieldError = (fieldName) => {
    if (errors[fieldName]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[fieldName];
        return newErrors;
      });
    }
  };

  const handleCourseSelect = (courseId) => {
    if (!courseId) {
      setFormData({
        ...formData,
        course: "",
        courseDuration: null,
        startDate: null,
        endDate: null,
      });
      setIsDropdownOpen(false);
      return;
    }
    const selectedCourse = courses.find(
      (course) => course.course_id === Number(courseId),
    );
    const duration = selectedCourse?.duration || null;
    setFormData({
      ...formData,
      course: courseId,
      courseDuration: duration,
      startDate: null,
      endDate: null,
    });
    clearFieldError("course");
    setIsDropdownOpen(false);
  };

  const getSelectedCourseTitle = () => {
    if (!formData.course) return "Select";
    const course = courses.find((c) => c.course_id === Number(formData.course));
    return course?.title || "Select";
  };

  return (
    <div className="min-h-screen bg-gray-50 py-6 px-4">
      {toast.show && (
        <ToastMessage
          type={toast.type}
          message={toast.message}
          onClose={closeToast}
          duration={3000}
        />
      )}

      <div className="max-w-5xl mx-auto bg-white rounded-lg shadow-sm p-8">
        <div className="text-center mb-6">
          <h1 className="text-3xl font-bold text-zinc-800 mb-1">
            Create Schedule
          </h1>
          <p className="text-sm text-slate-600">
            Complete the form and publish your Schedule instantly
          </p>
        </div>

        <div>
          <div className="mb-6">
            <h2 className="flex items-start text-base font-medium text-zinc-800 mb-4">
              Schedule Information
            </h2>

            <div className="mb-5">
              <div className="flex gap-6 items-start">
                {/* Course Dropdown */}
                <div className="flex flex-col gap-1">
                  <label className="flex flex-row items-start mb-2">
                    <span className="text-sm font-bold text-zinc-800">
                      Course / Subject Selection
                    </span>
                    <span className="text-sm font-bold text-red-600 ml-1">
                      *
                    </span>
                  </label>

                  <div className="relative w-64" ref={dropdownRef}>
                    <button
                      type="button"
                      onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                      className={`flex justify-between items-center w-full px-3 py-2 bg-white rounded shadow-sm border text-sm text-zinc-800 focus:outline-none focus:ring-2 focus:ring-[#4AACC5] ${
                        errors.course ? "border-red-500" : "border-zinc-200"
                      }`}
                    >
                      <span
                        className={
                          formData.course ? "text-zinc-800" : "text-zinc-400"
                        }
                      >
                        {getSelectedCourseTitle()}
                      </span>
                      <svg
                        className={`w-4 h-4 transition-transform ${isDropdownOpen ? "rotate-180" : ""}`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 9l-7 7-7-7"
                        />
                      </svg>
                    </button>

                    {isDropdownOpen && (
                      <div className="absolute z-50 w-full mt-1 bg-white border border-zinc-200 rounded shadow-lg max-h-60 overflow-y-auto">
                        <div
                          onClick={() => handleCourseSelect("")}
                          className="flex items-start px-3 py-2 text-sm text-zinc-800 hover:bg-gray-100 cursor-pointer"
                        >
                          Select
                        </div>
                        {courses.length === 0 ? (
                          <div className="px-3 py-2 text-sm text-gray-500">
                            Create the Course First
                          </div>
                        ) : (
                          courses.map((course) => (
                            <div
                              key={course.course_id}
                              onClick={() =>
                                handleCourseSelect(course.course_id)
                              }
                              className={`flex items-start px-3 py-2 text-sm cursor-pointer hover:bg-gray-100 ${
                                formData.course === String(course.course_id)
                                  ? "bg-blue-50 text-[#4AACC5] font-medium"
                                  : "text-zinc-800"
                              }`}
                            >
                              {course.title}
                            </div>
                          ))
                        )}
                      </div>
                    )}
                  </div>
                  {errors.course && (
                    <p className="mt-1 text-sm text-red-600">{errors.course}</p>
                  )}
                </div>

                {/* ✅ Instructor Dropdown — right of course, only for admin roles */}
                {isAdminRole && (
                  <div className="flex flex-col gap-1">
                    <label className="flex flex-row items-start mb-2">
                      <span className="text-sm font-bold text-zinc-800">
                        Select Instructor
                      </span>
                      <span className="text-sm font-bold text-red-600 ml-1">
                        *
                      </span>
                    </label>
                    <select
                      value={formData.instructor_id}
                      onChange={(e) => {
                        setFormData({
                          ...formData,
                          instructor_id: e.target.value,
                        });
                        clearFieldError("instructor_id");
                      }}
                      className={`w-64 px-3 py-2 bg-white rounded shadow-sm border text-sm text-zinc-800 
            focus:outline-none focus:ring-2 focus:ring-[#4AACC5] ${
              errors.instructor_id ? "border-red-500" : "border-zinc-200"
            }`}
                    >
                      <option value="">
                        {instructors.length === 0
                          ? "No instructors found"
                          : "Select Instructor"}
                      </option>
                      {instructors.map((instructor) => (
                        <option
                          key={instructor.user_id}
                          value={instructor.user_id}
                        >
                          {instructor.full_name}
                        </option>
                      ))}
                    </select>
                    {errors.instructor_id && (
                      <p className="mt-1 text-sm text-red-600">
                        {errors.instructor_id}
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>

            <div className="grid grid-cols-3 gap-6">
              <div>
                <label className="flex flex-row items-start mb-2">
                  <span className="text-sm font-bold text-zinc-800">Mode</span>
                  <span className="text-sm font-bold text-red-600 ml-1">*</span>
                </label>
                <select
                  value={formData.mode}
                  onChange={(e) =>
                    setFormData({ ...formData, mode: e.target.value })
                  }
                  className="w-full px-3 py-2 bg-white rounded shadow-sm border border-zinc-200 text-sm text-zinc-800 focus:outline-none focus:ring-2 focus:ring-[#4AACC5]"
                >
                  <option value="In Person / Regular">
                    In Person / Regular
                  </option>
                  <option value="Virtual">Virtual</option>
                </select>
              </div>

              <div>
                <label className="flex flex-row items-start mb-2">
                  <span className="text-sm font-bold text-zinc-800">
                    Location / Meeting Link
                  </span>
                  <span className="text-sm font-bold text-red-600 ml-1">*</span>
                </label>
                <input
                  type="text"
                  value={formData.location}
                  onChange={(e) => {
                    setFormData({ ...formData, location: e.target.value });
                    clearFieldError("location");
                  }}
                  placeholder="Enter Location or Meeting Link"
                  className={`w-full px-3 py-2 bg-white rounded shadow-sm border text-sm text-zinc-800 placeholder-zinc-400 focus:outline-none focus:ring-2 focus:ring-[#4AACC5] ${
                    errors.location ? "border-red-500" : "border-zinc-200"
                  }`}
                />
                {errors.location && (
                  <p className="mt-1 text-sm text-red-600">{errors.location}</p>
                )}
              </div>
            </div>
          </div>

          <hr className="border-gray-200 my-6" />

          <div className="mb-6">
            <h2 className="flex items-start text-base font-medium text-zinc-800 mb-4">
              Schedule Date and Time
            </h2>

            <div className="grid grid-cols-3 gap-6 mb-5">
              <div className="flex flex-col">
                <label className="flex items-center mb-2">
                  <span className="text-sm font-bold text-zinc-800">
                    Start Date
                  </span>
                  <span className="text-sm font-bold text-red-600 ml-1">*</span>
                </label>
                <DatePicker
                  selected={formData.startDate}
                  onChange={(date) => {
                    let autoCalculatedEndDate = null;
                    if (date && formData.courseDuration) {
                      autoCalculatedEndDate = new Date(date);
                      let durationInDays = 0;
                      const duration = String(formData.courseDuration)
                        .toLowerCase()
                        .trim();

                      if (duration.includes("-")) {
                        const parts = duration.split("-");
                        const number = parseInt(parts[0]) || 0;
                        const unit = parts[1];
                        if (unit === "day" || unit === "days")
                          durationInDays = number;
                        else if (unit === "month" || unit === "months")
                          durationInDays = number * 30;
                        else if (unit === "year" || unit === "years")
                          durationInDays = number * 365;
                      } else if (
                        duration === "one_month" ||
                        duration === "1 month"
                      )
                        durationInDays = 30;
                      else if (
                        duration === "two_months" ||
                        duration === "2 months"
                      )
                        durationInDays = 60;
                      else if (
                        duration === "three_months" ||
                        duration === "3 months"
                      )
                        durationInDays = 90;
                      else if (
                        duration === "six_months" ||
                        duration === "6 months"
                      )
                        durationInDays = 180;
                      else if (duration === "one_year" || duration === "1 year")
                        durationInDays = 365;
                      else
                        durationInDays = Number(formData.courseDuration) || 0;

                      if (durationInDays > 0) {
                        autoCalculatedEndDate.setDate(
                          autoCalculatedEndDate.getDate() +
                            (durationInDays - 1),
                        );
                      } else {
                        autoCalculatedEndDate = null;
                      }
                    }
                    setFormData({
                      ...formData,
                      startDate: date,
                      endDate: autoCalculatedEndDate,
                    });
                    clearFieldError("startDate");
                  }}
                  dateFormat="MM/dd/yyyy"
                  placeholderText="MM/dd/yyyy"
                  minDate={
                    new Date(new Date().setDate(new Date().getDate() + 1))
                  }
                  calendarStartDay={0}
                  wrapperClassName="w-full block"
                  className={`block w-full px-3 py-2 bg-white rounded border text-sm text-left focus:outline-none focus:ring-2 focus:ring-[#4AACC5] ${
                    errors.startDate ? "border-red-500" : "border-gray-300"
                  }`}
                />
                {errors.startDate && (
                  <p className="mt-1 text-sm text-red-600">
                    {errors.startDate}
                  </p>
                )}
              </div>

              <div className="flex flex-col">
                <label className="flex items-center mb-2">
                  <span className="text-sm font-bold text-zinc-800">
                    End Date
                  </span>
                  <span className="text-sm font-bold text-red-600 ml-1">*</span>
                </label>
                <DatePicker
                  selected={formData.endDate}
                  onChange={(date) => {
                    setFormData({ ...formData, endDate: date });
                    clearFieldError("endDate");
                  }}
                  dateFormat="MM/dd/yyyy"
                  placeholderText="MM/dd/yyyy"
                  minDate={formData.startDate}
                  disabled={!formData.startDate}
                  wrapperClassName="w-full block"
                  className={`block w-full px-3 py-2 rounded border text-sm text-left ${
                    !formData.startDate
                      ? "bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed"
                      : errors.endDate
                        ? "bg-white border-red-500"
                        : "bg-white border-gray-300"
                  }`}
                />
                {errors.endDate && (
                  <p className="mt-1 text-sm text-red-600">{errors.endDate}</p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-3 gap-6 mb-5">
              <div>
                <label className="flex flex-row items-start mb-2">
                  <span className="text-sm font-bold text-zinc-800">
                    Start Time
                  </span>
                  <span className="text-sm font-bold text-red-600 ml-1">*</span>
                </label>
                <input
                  type="time"
                  value={formData.startTime}
                  onChange={(e) => {
                    setFormData({ ...formData, startTime: e.target.value });
                    clearFieldError("startTime");
                  }}
                  className={`w-full px-3 py-2 bg-white rounded shadow-sm border text-sm text-zinc-800 focus:outline-none focus:ring-2 focus:ring-[#4AACC5] ${
                    errors.startTime ? "border-red-500" : "border-zinc-200"
                  }`}
                />
                {errors.startTime && (
                  <p className="mt-1 text-sm text-red-600">
                    {errors.startTime}
                  </p>
                )}
              </div>

              <div>
                <label className="flex flex-row items-start mb-2">
                  <span className="text-sm font-bold text-zinc-800">
                    End Time
                  </span>
                  <span className="text-sm font-bold text-red-600 ml-1">*</span>
                </label>
                <input
                  type="time"
                  value={formData.endTime}
                  onChange={(e) => {
                    setFormData({ ...formData, endTime: e.target.value });
                    clearFieldError("endTime");
                  }}
                  className={`w-full px-3 py-2 bg-white rounded shadow-sm border text-sm text-zinc-800 focus:outline-none focus:ring-2 focus:ring-[#4AACC5] ${
                    errors.endTime ? "border-red-500" : "border-zinc-200"
                  }`}
                />
                {errors.endTime && (
                  <p className="mt-1 text-sm text-red-600">{errors.endTime}</p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-3 gap-6">
              <div>
                <label className="flex flex-row items-start mb-2">
                  <span className="text-sm font-bold text-zinc-800">
                    Enable Reminders
                  </span>
                </label>
                <div className="flex items-center gap-3">
                  <span className="text-sm text-zinc-800">Enable</span>
                  <button
                    type="button"
                    onClick={() =>
                      setFormData({
                        ...formData,
                        enableReminders: !formData.enableReminders,
                      })
                    }
                    className={`relative w-11 h-6 rounded-full transition-colors ${
                      formData.enableReminders ? "bg-cyan-500" : "bg-gray-300"
                    }`}
                  >
                    <span
                      className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full transition-transform ${
                        formData.enableReminders
                          ? "translate-x-5"
                          : "translate-x-0"
                      }`}
                    />
                  </button>
                </div>
              </div>

              <div>
                <label className="flex flex-row items-start mb-2">
                  <span className="text-sm font-bold text-zinc-800">
                    Reminder time
                  </span>
                </label>
                <select
                  value={formData.reminderTime}
                  onChange={(e) =>
                    setFormData({ ...formData, reminderTime: e.target.value })
                  }
                  className="w-full px-3 py-2 bg-white rounded shadow-sm border border-zinc-200 text-sm text-zinc-800 focus:outline-none focus:ring-2 focus:ring-[#4AACC5]"
                  disabled={!formData.enableReminders}
                >
                  <option value="">Select Reminder</option>
                  <option value="30min">30 min before</option>
                  <option value="1hr">1 hour before</option>
                  <option value="2hr">2 hours before</option>
                  <option value="1day">1 day before</option>
                </select>
              </div>
            </div>
          </div>

          <hr className="border-gray-200 my-6" />

          <div className="mb-6">
            <h2 className="flex items-start text-base font-medium text-zinc-800 mb-4">
              Program Price Information
            </h2>

            <div className="grid grid-cols-3 gap-6 mb-5">
              <div>
                <label className="flex flex-row items-start mb-2">
                  <span className="text-sm font-bold text-zinc-800">
                    Discount applicable
                  </span>
                </label>
                <div className="flex items-center gap-3">
                  <span className="text-sm text-zinc-800">Enable</span>
                  <button
                    type="button"
                    onClick={() =>
                      setFormData({
                        ...formData,
                        discountApplicable: !formData.discountApplicable,
                      })
                    }
                    className={`relative w-11 h-6 rounded-full transition-colors ${
                      formData.discountApplicable
                        ? "bg-cyan-500"
                        : "bg-gray-300"
                    }`}
                  >
                    <span
                      className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full transition-transform ${
                        formData.discountApplicable
                          ? "translate-x-5"
                          : "translate-x-0"
                      }`}
                    />
                  </button>
                </div>
              </div>

              <div>
                <label className="flex flex-row items-start mb-2">
                  <span className="text-sm font-bold text-zinc-800">
                    Discount Type
                  </span>
                  <span className="text-sm font-bold text-red-600 ml-1">*</span>
                </label>
                <select
                  value={formData.discountType}
                  onChange={(e) => {
                    setFormData({ ...formData, discountType: e.target.value });
                    clearFieldError("discountType");
                  }}
                  className={`w-full px-3 py-2 bg-white rounded shadow-sm border text-sm text-zinc-800 focus:outline-none focus:ring-2 focus:ring-[#4AACC5] ${
                    errors.discountType ? "border-red-500" : "border-zinc-200"
                  }`}
                  disabled={!formData.discountApplicable}
                >
                  <option value="">Select Discount</option>
                  <option value="Percentage">Percentage</option>
                  <option value="FlatAmount">Fixed Amount</option>
                </select>
                {errors.discountType && (
                  <p className="mt-1 text-sm text-red-600">
                    {errors.discountType}
                  </p>
                )}
              </div>

              <div>
                <label className="flex flex-row items-start mb-2">
                  <span className="text-sm font-bold text-zinc-800">
                    Discount Value
                  </span>
                  <span className="text-sm font-bold text-red-600 ml-1">*</span>
                </label>
                <div className="relative">
                  <input
                    type="number"
                    value={formData.discountValue}
                    onChange={(e) => {
                      setFormData({
                        ...formData,
                        discountValue: e.target.value,
                      });
                      clearFieldError("discountValue");
                    }}
                    placeholder="Enter Discount Value"
                    className={`w-full px-3 py-2 bg-white rounded shadow-sm border text-sm text-zinc-800 placeholder-zinc-400 focus:outline-none focus:ring-2 focus:ring-[#4AACC5] ${
                      errors.discountValue
                        ? "border-red-500"
                        : "border-zinc-200"
                    } ${formData.discountType && formData.discountApplicable ? "pr-8" : ""}`}
                    disabled={!formData.discountApplicable}
                  />
                  {formData.discountType && formData.discountApplicable && (
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm font-bold text-zinc-600 pointer-events-none">
                      {formData.discountType === "Percentage" ? "%" : "$"}
                    </span>
                  )}
                </div>
                {errors.discountValue && (
                  <p className="mt-1 text-sm text-red-600">
                    {errors.discountValue}
                  </p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-3 gap-6">
              <div>
                <label className="flex flex-row items-start mb-2">
                  <span className="text-sm font-bold text-zinc-800">
                    Discount Valid From
                  </span>
                  <span className="text-sm font-bold text-red-600 ml-1">*</span>
                </label>
                <DatePicker
                  selected={formData.discountValidFrom}
                  onChange={(date) => {
                    setFormData({
                      ...formData,
                      discountValidFrom: date,
                      discountValidTo: null,
                    });
                    clearFieldError("discountValidFrom");
                  }}
                  dateFormat="MM/dd/yyyy"
                  placeholderText="MM/DD/YYYY"
                  disabled={!formData.discountApplicable}
                  minDate={new Date()}
                  maxDate={
                    formData.startDate
                      ? new Date(formData.startDate.getTime() - 86400000)
                      : null
                  }
                  wrapperClassName="w-full block"
                  className={`block w-full px-3 py-2 text-sm rounded border text-left ${
                    !formData.discountApplicable
                      ? "bg-gray-100 cursor-not-allowed border-gray-200"
                      : errors.discountValidFrom
                        ? "bg-white border-red-500"
                        : "bg-white border-gray-300"
                  }`}
                />
                {errors.discountValidFrom && (
                  <p className="mt-1 text-sm text-red-600">
                    {errors.discountValidFrom}
                  </p>
                )}
              </div>

              <div className="flex flex-col items-start">
                <label className="mb-2 text-sm font-bold text-zinc-800">
                  Discount Valid To{" "}
                  <span className="text-sm font-bold text-red-600 ml-1">*</span>
                </label>
                <div className="w-full">
                  <DatePicker
                    selected={formData.discountValidTo}
                    onChange={(date) => {
                      setFormData({ ...formData, discountValidTo: date });
                      clearFieldError("discountValidTo");
                    }}
                    dateFormat="MM/dd/yyyy"
                    placeholderText="MM/DD/YYYY"
                    minDate={formData.discountValidFrom}
                    maxDate={
                      formData.startDate
                        ? new Date(formData.startDate.getTime() - 86400000)
                        : null
                    }
                    disabled={
                      !formData.discountApplicable ||
                      !formData.discountValidFrom
                    }
                    wrapperClassName="w-full block"
                    className={`block w-full px-3 py-2 text-sm rounded border text-left ${
                      !formData.discountApplicable
                        ? "bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed"
                        : errors.discountValidTo
                          ? "bg-white border-red-500"
                          : "bg-white text-zinc-800 border-gray-300"
                    }`}
                  />
                </div>
                {errors.discountValidTo && (
                  <p className="mt-1 text-sm text-red-600">
                    {errors.discountValidTo}
                  </p>
                )}
              </div>
            </div>
          </div>

          <div className="flex gap-4 mt-8">
            <button
              onClick={handleCancel}
              className="flex-1 max-w-xs px-5 py-2.5 bg-white rounded border-2 border-[#4AACC5] text-sm font-bold text-[#4AACC5] hover:bg-teal-50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={loading}
              className={`flex-1 max-w-xs px-5 py-2.5 rounded text-sm font-bold text-white transition-colors ${
                loading
                  ? "bg-gray-300 cursor-not-allowed"
                  : "bg-[#4AACC5] hover:bg-[#72bed1]"
              }`}
            >
              {loading ? "Saving..." : "Save Schedule"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateScheduleForm;
